import { useRef, useState } from "react";
import { Bold, Italic, Link as LinkIcon, List, Eye, Pencil } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Textarea } from "@/components/ui/textarea";

// Custom renderer for links: opens externally, styled in Perx gold.
// Defined outside the component so React doesn't re-mount on each render.
const MARKDOWN_LINK = ({ node, ...props }) => (
    <a
        {...props}
        target="_blank"
        rel="noopener noreferrer"
        className="text-gold-600 underline underline-offset-2 hover:text-gold-700"
    />
);

const ALLOWED_ELEMENTS = ["p", "a", "strong", "em", "code", "ul", "ol", "li", "br"];

/**
 * Lightweight Markdown field for the admin console.
 *
 * Deliberately minimal:
 *   • Native textarea for editing (no contenteditable weirdness).
 *   • Tiny toolbar that inserts / wraps markdown syntax at the cursor
 *     — Bold (**x**), Italic (*x*), Link ([text](url)), Bullet list.
 *   • Preview toggle rendered via `react-markdown` with a small
 *     `allowedElements` whitelist so ops can't accidentally paste raw
 *     HTML or `<script>` in.
 *
 * Renders + wrapping helpers live here rather than being pulled in from
 * a bloated WYSIWYG bundle — keeps the admin console fast to load.
 */
export default function MarkdownField({
    value,
    onChange,
    placeholder,
    rows = 5,
    testid = "markdown-field",
}) {
    const textareaRef = useRef(null);
    const [mode, setMode] = useState("write"); // "write" | "preview"

    const wrap = (before, after = before) => {
        const ta = textareaRef.current;
        if (!ta) return;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const selected = value.slice(start, end);
        const next = value.slice(0, start) + before + selected + after + value.slice(end);
        onChange(next);
        // Restore focus + selection just inside the wrappers.
        requestAnimationFrame(() => {
            ta.focus();
            ta.setSelectionRange(start + before.length, end + before.length);
        });
    };

    const insertLink = () => {
        const url = window.prompt("Link URL", "https://");
        if (!url) return;
        const ta = textareaRef.current;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const selected = value.slice(start, end) || "link text";
        const md = `[${selected}](${url})`;
        onChange(value.slice(0, start) + md + value.slice(end));
        requestAnimationFrame(() => {
            ta.focus();
            const linkStart = start + 1;
            ta.setSelectionRange(linkStart, linkStart + selected.length);
        });
    };

    const insertBullet = () => {
        const ta = textareaRef.current;
        const start = ta.selectionStart;
        // Insert a fresh bullet on its own line — collapse any current selection.
        const prefix = value.slice(0, start);
        const suffix = value.slice(ta.selectionEnd);
        const needsNewline = prefix.length > 0 && !prefix.endsWith("\n");
        const bullet = `${needsNewline ? "\n" : ""}- `;
        onChange(prefix + bullet + suffix);
        requestAnimationFrame(() => {
            ta.focus();
            const cursor = prefix.length + bullet.length;
            ta.setSelectionRange(cursor, cursor);
        });
    };

    return (
        <div className="rounded-xl border border-navy-100 bg-white overflow-hidden" data-testid={testid}>
            <div className="flex items-center justify-between border-b border-navy-100 bg-cream/40 px-2 py-1.5">
                <div className="flex items-center gap-0.5">
                    <ToolbarBtn
                        title="Bold"
                        onClick={() => wrap("**")}
                        testid={`${testid}-bold`}
                    >
                        <Bold size={13} />
                    </ToolbarBtn>
                    <ToolbarBtn
                        title="Italic"
                        onClick={() => wrap("*")}
                        testid={`${testid}-italic`}
                    >
                        <Italic size={13} />
                    </ToolbarBtn>
                    <ToolbarBtn
                        title="Link"
                        onClick={insertLink}
                        testid={`${testid}-link`}
                    >
                        <LinkIcon size={13} />
                    </ToolbarBtn>
                    <ToolbarBtn
                        title="Bullet list"
                        onClick={insertBullet}
                        testid={`${testid}-bullet`}
                    >
                        <List size={13} />
                    </ToolbarBtn>
                </div>
                <div className="flex items-center gap-0.5">
                    <ToolbarBtn
                        title="Write"
                        onClick={() => setMode("write")}
                        active={mode === "write"}
                        testid={`${testid}-write-mode`}
                    >
                        <Pencil size={12} />
                        <span className="ml-1 text-[11px]">Write</span>
                    </ToolbarBtn>
                    <ToolbarBtn
                        title="Preview"
                        onClick={() => setMode("preview")}
                        active={mode === "preview"}
                        testid={`${testid}-preview-mode`}
                    >
                        <Eye size={12} />
                        <span className="ml-1 text-[11px]">Preview</span>
                    </ToolbarBtn>
                </div>
            </div>
            {mode === "write" ? (
                <Textarea
                    ref={textareaRef}
                    rows={rows}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    data-testid={`${testid}-textarea`}
                    className="border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 font-mono text-[13px] leading-relaxed"
                />
            ) : (
                <div
                    className="px-3 py-3 min-h-[8em] text-[13px] text-navy-600 leading-relaxed prose prose-sm max-w-none"
                    data-testid={`${testid}-preview`}
                >
                    {value?.trim() ? (
                        <ReactMarkdown
                            components={{ a: MARKDOWN_LINK }}
                            allowedElements={ALLOWED_ELEMENTS}
                            unwrapDisallowed
                        >
                            {value}
                        </ReactMarkdown>
                    ) : (
                        <span className="text-navy-400 italic">
                            Nothing to preview yet.
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

function ToolbarBtn({ children, onClick, title, active, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={title}
            data-testid={testid}
            className={`inline-flex items-center rounded-md px-2 py-1 text-navy-600 hover:bg-white transition-colors ${
                active ? "bg-white text-navy-700 shadow-soft" : ""
            }`}
        >
            {children}
        </button>
    );
}
